import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import ReactSVG from 'react-svg';
import {
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
} from '@material-ui/core';
import History from '@material-ui/icons/History';
import { useTryToCall } from '~/hooks';
import { eventTracker } from '~/helpers';
import * as NuacomComponent from '~/components';
import * as Loaders from '~/components/Loaders';
import * as actions from '~/store/actions';
import { AddIcon } from '~/assets/svgIcons';
import * as lib from '~/dependencies';
import ListContacts from './components/ListContacts';
import TabContacts from './components/Tab';

import {
  AddBoxIconComponent,
  Container,
  Block,
  EmptyStateComponent,
  LoadingContainer,
  NoContent,
  BoxBtn,
} from './styles';

export default function ContactBox(props) {
  const { match, location } = props;

  const [search, setSearch] = useState('');
  const [selectContact, setSelectContact] = useState(false);
  const [numbersSelected, setNumbersSelected] = useState([]);
  const [open, setOpen] = useState(false);
  const [searchBox, setSearchBox] = useState(false);
  const [tab, setTab] = useState(location?.state?.tab || 0);

  const loadingData = useSelector(state => state?.contact?.loading);
  const currentCalls = useSelector(state => state?.callCurrent?.data);
  const activeSession = useSelector(state => state?.callCurrent?.session);
  const contactsDataAll = useSelector(state => state?.contact?.dataAll);
  const contactsDataPersonal = useSelector(
    state => state?.contact?.dataPersonal
  );

  const dispatch = useDispatch();
  const tryToCall = useTryToCall();
  const { t } = lib.useTranslation();

  const findValue = useCallback(text => setSearch(text), [setSearch]);
  const setNumber = useCallback(
    value => {
      dispatch(actions.numberContactToTransfer(value));
    },
    [dispatch]
  );

  useEffect(() => {
    const activeIndex = activeSession
      ? currentCalls.findIndex(e => e.id === activeSession.id)
      : -1;
    const callInProgress = activeIndex !== -1 && currentCalls[activeIndex];

    if (callInProgress) {
      if (callInProgress.callActions.transfer) {
        setSelectContact(true);
      } else {
        setSelectContact(false);
      }
    }
  }, [currentCalls, activeSession]);

  useEffect(() => {
    if (!open) setNumbersSelected('');
  }, [open]);

  useEffect(() => {
    dispatch(actions.checkNewContacts('Personal', false, false));
    dispatch(actions.checkNewContacts('All', false, false));
  }, [tab, dispatch]);

  function handleContactsBtnSearchClick(value) {
    setSearchBox(value);
    eventTracker(events => events.ContactSearchClicked);
  }

  function handleOnSearchChange(text) {
    if (text !== null) {
      setSearch(text);
    }
  }

  function selectNumber({ mobile, landline }) {
    setNumbersSelected({ mobile, landline });
    setOpen(true);
  }

  function addNumberToTransfer(number) {
    setNumber(number);
    setOpen(false);
  }

  function filtered(searchString) {
    // depending of tab choose allcontact or my contacts
    const selectedContacts = tab === 0 ? contactsDataPersonal : contactsDataAll;

    if (searchString) {
      if (searchString.length >= 3) {
        const allContacts = selectedContacts.reduce(
          (prev, next) => prev.concat(next.data),
          []
        );

        const filteredContacts = allContacts.filter(
          e =>
            e.name.toUpperCase().includes(searchString.toUpperCase()) ||
            e.mobile.includes(searchString.toUpperCase()) ||
            e.landline.includes(searchString.toUpperCase())
        );

        const normalizedContacts = filteredContacts.map(e => {
          let letter;
          if (/^[a-zA-Z]+$/.test(e.name)) {
            letter = e.name[0].toUpperCase();
          } else {
            letter = '#';
          }

          return {
            id: e.id,
            name: `${e.name}`,
            letter,
            landline: e.landline,
            email: e.email,
            mobile: e.mobile,
            address1: e.address1,
            address2: e.address2,
            address3: e.address3,
            address4: e.address4,
            birthday: e.birthday,
          };
        });

        const groupedContacts = _(normalizedContacts)
          .orderBy(o => o.letter)
          .groupBy(o => o.letter)
          .map((data, letter) => ({ title: letter, data }))
          .value();

        return groupedContacts;
      }
    }
    return selectedContacts;
  }

  const contactList = filtered(search);

  return (
    <Container>
      <Dialog
        onClose={() => setOpen(false)}
        aria-labelledby="simple-dialog-title"
        open={open}
        style={{ zIndex: 99999 }}>
        <span style={{ fontSize: 14, fontWeight: 500, padding: 20 }}>
          {t('screens.contacts.selectNumber')}
        </span>
        <List>
          {numbersSelected && numbersSelected.mobile && (
            <>
              <span style={{ fontSize: 10, marginLeft: 18 }}>
                {t('screens.contacts.mobile')}
              </span>
              <ListItem
                button
                onClick={() => addNumberToTransfer(numbersSelected.mobile)}
                key={numbersSelected.mobile}
                style={{ height: 40 }}>
                <ListItemAvatar>
                  <AddBoxIconComponent />
                </ListItemAvatar>
                <ListItemText primary={numbersSelected.mobile} />
              </ListItem>
            </>
          )}

          {numbersSelected && numbersSelected.landline && (
            <>
              <span style={{ fontSize: 10, marginLeft: 18 }}>
                {t('screens.contacts.landline')}
              </span>
              <ListItem
                button
                onClick={() => addNumberToTransfer(numbersSelected.landline)}
                key={numbersSelected.landline}
                style={{ height: 40 }}>
                <ListItemAvatar>
                  <AddBoxIconComponent />
                </ListItemAvatar>
                <ListItemText primary={numbersSelected.landline} />
              </ListItem>
            </>
          )}
        </List>
      </Dialog>

      <NuacomComponent.Header {...props}>
        {!searchBox ? (
          <>
            <TabContacts setTab={setTab} tab={tab} isAdmin />
            <lib.Link
              to={{
                pathname: `${match.path}/addNewContact`,
                state: { edit: false, tab },
              }}>
              <BoxBtn>
                <ReactSVG src={AddIcon} className="icon" />
              </BoxBtn>
            </lib.Link>
            <NuacomComponent.BtnSearch
              callback={handleContactsBtnSearchClick}
              value={searchBox}
            />
          </>
        ) : (
          <NuacomComponent.ContactsSearch
            value={search}
            onChange={handleOnSearchChange}
            setContacts={findValue}
            callback={setSearchBox}
          />
        )}
      </NuacomComponent.Header>

      <Block>
        {contactList && contactList.length > 0 && !loadingData && (
          <ListContacts
            tab={tab}
            listContact={contactList}
            selectContact={selectContact}
            match={match}
            selectNumber={selectNumber}
            tryToCall={tryToCall}
            scrollToIndex={location?.state?.lastContactDetailsIndex}
          />
        )}
        {contactList && contactList.length === 0 && !loadingData && (
          <EmptyStateComponent>
            <NoContent>
              <History />
              <Typography>{t('screens.contacts.noContacts')}</Typography>
            </NoContent>
          </EmptyStateComponent>
        )}

        {loadingData && (
          <EmptyStateComponent>
            <NoContent>
              <LoadingContainer>
                <Loaders.List numRows={8} />
              </LoadingContainer>
            </NoContent>
          </EmptyStateComponent>
        )}
      </Block>
    </Container>
  );
}

ContactBox.defaultProps = {
  match: [],
  location: [],
};

ContactBox.propTypes = {
  match: lib.PropTypes.instanceOf(Object),
  location: lib.PropTypes.instanceOf(Object),
};
